import { useState, useCallback, useRef, useEffect } from 'react';
import useAxiosPrivate from './useAxiosPrivate';
import API_URLS from '../constants/apiConstants';
import { useChatGroupWebSocket } from './useChatGroupWebSocket';
import useAuth from './useAuth';

export const useMessages = (groupName) => {
  const axiosPrivate = useAxiosPrivate();
  const { school } = useAuth();
  const [messages, setMessages] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [justSentMessage, setJustSentMessage] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [cursor, setCursor] = useState(null);

  const isLoadingRef = useRef(false);  // Use refs to prevent infinite loops
  const { isConnected, onMessage, sendMessage } = useChatGroupWebSocket(groupName);

  // Handle WebSocket of chat group messages
  useEffect(() => {
    if (!isConnected || !groupName) return;

    const handleNewMessage = (data) => {
      const newMessage = data.message;
      setMessages((prev) => [...prev, newMessage]);
      setJustSentMessage(true);
    };

    const unsubscribe = onMessage(handleNewMessage);
    return unsubscribe;
  }, [groupName, isConnected, onMessage]);

  const fetchMessages = useCallback(async (pageNum = 1, shouldAppend = false, cursor = null) => {
    // Use ref for loading state to avoid dependency issues
    if (!groupName || isLoadingRef.current) {
      return;
    }
    
    isLoadingRef.current = true;
    setIsLoading(true);
    
    try {
      let api_url = API_URLS.getGroupMessages(school.id, groupName);
      if (cursor) {
        api_url = API_URLS.getGroupMessages(school.id, groupName, cursor);
      }
      const response = await axiosPrivate.get(api_url);
      const data = response.data;
      setCursor(data.next_cursor);  // Update cursor for next fetch

      if (data.results && data.results.length > 0) {
        setMessages(prev => shouldAppend ? [...data.results, ...prev] : data.results);
        
        // Check if there are more pages
        const hasNextPage = data.next !== null;
        setHasMore(hasNextPage);
        
        if (shouldAppend) {
          setPage(pageNum);
        }
      } else {
        setHasMore(false);
        if (!shouldAppend) {
          setMessages([]);
        }
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setHasMore(false);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [groupName, school.id, axiosPrivate]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingRef.current) return;
    setIsLoadingMore(true);
    fetchMessages(page + 1, true, cursor);
    setIsLoading(false);
  }, [hasMore, fetchMessages, page, cursor]);

  const refreshMessages = useCallback(() => {
    if (!groupName) return;
    
    setPage(1);
    setHasMore(true);
    fetchMessages(1, false);
  }, [groupName, fetchMessages]);

  const handleSendMessageViaAPI = async (newMessage, file = null) => {
    if (!newMessage && !file) return false;

    setIsUploading(true);
    try {
      const formData = new FormData();
      
      // Add message text if provided
      if (newMessage && newMessage.trim()) {
        formData.append('body', newMessage.trim());
      }
      // Add file if provided
      if (file) {
        formData.append('file', file);
        formData.append('file_name', file.name);
        formData.append('file_type', file.type);
        formData.append('file_size', file.size);
      }

      await axiosPrivate.post(
        API_URLS.sendGroupMessage(school.id, groupName),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(progress);
            }
          },
        }
      );

      return true;
    } catch (error) {
      console.error("Failed to send message:", error);
      return false;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSendMessageViaWebSocket = async (newMessage) => {
    if (!newMessage || !groupName) return;

    try {
      const messageData = { body: newMessage.trim() };
      const sent = sendMessage(messageData);
      return sent;
    } catch (error) {
      console.error("Failed to send message:", error);
    }

    return false;
  };

  const handleSendMessage = (newMessage, file) => {
    if (!newMessage.trim() && !file) return;

    if (file) {
      return handleSendMessageViaAPI(newMessage, file);
    } else {
      return handleSendMessageViaWebSocket(newMessage);
    }
  };

  return {
    messages,
    page,
    hasMore,
    isLoading,
    isUploading,
    uploadProgress,
    isLoadingMore,
    loadMore,
    refreshMessages,
    isConnected,
    justSentMessage,
    setJustSentMessage,
    handleSendMessage,
  };
};
